import React, { useCallback, useEffect, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import Modal from "../../Modal";
import ScreenCard from "../../../screens/ScreenCard";
import SimpleHeader from "../../SimpleHeader";
import { Controller, useForm } from "react-hook-form";
import { useI18n } from "../../../hooks/i18n-hooks";
import { useTheme } from "../../../hooks/use-theme";
import { formatCurrency, toFixedIfNecessary } from "../../../utils";
import MarkdownView from "../../MarkdownView";
import VStack from "../../../ui_atoms/VStack";
import { BodyMediumText, BodySmallText } from "../../FormattedText";
import QuantityField from "../../QuantityField";
import { useOpenMarkdownLink } from "../../../hooks/use-open-markdown-link";

type FormData = {
    nbHours?: number;
};

type OvertimePaidCreateModalProps = {
    hourlyInvoiceAmount: number;
    initialData?: number;
    formattedEndOfMonthOvertimeTotal: number;
    update: (overtimePaid100?: number) => void;
    onClose: () => void;
    visible: boolean;
    cctEditionId?: string;
};

const OvertimePaidCreateModal: React.FC<OvertimePaidCreateModalProps> = ({
    hourlyInvoiceAmount,
    initialData,
    formattedEndOfMonthOvertimeTotal,
    onClose,
    update,
    visible,
    cctEditionId,
}) => {
    const styles = useStyles();
    const { colors, fonts } = useTheme();
    const i18n = useI18n();

    const {
        control,
        handleSubmit,
        formState: { isValid, errors },
        watch,
        reset,
        setValue,
    } = useForm<FormData>({
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            nbHours: undefined,
        },
    });

    const nbHours = watch("nbHours");

    useEffect(() => {
        if (visible) {
            if (initialData) {
                setValue("nbHours", initialData);
            }
        } else {
            reset();
        }
    }, [initialData, reset, setValue, visible]);

    const currentNbHours = useMemo(
        () => Number(formattedEndOfMonthOvertimeTotal) - (nbHours || 0),
        [nbHours, formattedEndOfMonthOvertimeTotal],
    );

    const handleCreateOvertimePaid100 = useCallback(
        (formData: FormData) => {
            onClose();
            if ((formData.nbHours || 0) !== 0) {
                update(formData.nbHours || 0);
            } else {
                update(undefined);
            }
        },
        [onClose, update],
    );

    const validateOvertimeWorkDelta = useCallback(
        (value?: number) => {
            if (!value) {
                return undefined;
            }
            return value > formattedEndOfMonthOvertimeTotal
                ? i18n.t("overtimePaid.overtime_paid_too_high", {
                      deltaWorkHours: formattedEndOfMonthOvertimeTotal,
                  })
                : undefined;
        },
        [formattedEndOfMonthOvertimeTotal, i18n],
    );

    const openMarkdownLink = useOpenMarkdownLink();

    return (
        <Modal visible={visible} onClose={onClose} hideCloseButton>
            <SimpleHeader title={i18n.t("overtimePaid.title")} onClose={onClose} mode="small" />
            <ScreenCard
                buttons={[
                    {
                        onPress: handleSubmit(handleCreateOvertimePaid100),
                        mode: "secondary",
                        isValid: isValid,
                        title: !initialData ? i18n.t("add") : i18n.t("edit"),
                    },
                ]}
                withAssistanceButton
            >
                <VStack gap="2/3" h="2/3">
                    <VStack gap="1/3">
                        <BodyMediumText>{i18n.t("overtimePaid.amount_title")}</BodyMediumText>
                        <MarkdownView
                            markdownStyle={{
                                paragraph: {
                                    ...fonts.bodySmall,
                                    color: colors.greyAccessible,
                                },
                            }}
                            markdown={`${i18n.t(`overtimePaid.description_intro_by_cct.${cctEditionId === "13190" ? "paysagiste" : "default"}`)} ${i18n.t("overtimePaid.description_end")}`}
                            onLinkPress={openMarkdownLink}
                        />
                    </VStack>
                    <View
                        style={[
                            styles.container,
                            currentNbHours === 0 && {
                                height: 0,
                                paddingBottom: 0,
                                overflow: "hidden",
                            },
                        ]}
                    >
                        <MarkdownView
                            fontColor={colors.secondary}
                            markdown={i18n.t("overtimePaid.delta_work_hours_info", {
                                symbol: currentNbHours > 0 ? "+" : "-",
                                deltaWorkHours: toFixedIfNecessary(Math.abs(currentNbHours), 2),
                                overtimeAmount: formatCurrency({
                                    amountInCents: hourlyInvoiceAmount * Math.abs(currentNbHours),
                                }),
                            })}
                        />
                    </View>
                    <Controller
                        name="nbHours"
                        control={control}
                        rules={{ required: true, validate: validateOvertimeWorkDelta }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <QuantityField
                                value={initialData || value}
                                onChange={onChange}
                                decimalPlaces={2}
                                error={error?.message}
                            />
                        )}
                    />
                    {nbHours && !errors.nbHours && (
                        <BodySmallText color={colors.greyAccessible}>
                            {i18n.t("overtimePaid.hours_equivalent", {
                                hours: nbHours,
                                amount: formatCurrency({
                                    amountInCents: nbHours * hourlyInvoiceAmount,
                                }),
                            })}
                        </BodySmallText>
                    )}
                </VStack>
            </ScreenCard>
        </Modal>
    );
};

const useStyles = () => {
    const { colors, gridSize, roundness } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    paddingHorizontal: gridSize / 2,
                    paddingBottom: gridSize / 5,
                    borderRadius: roundness,
                    backgroundColor: colors.onSecondaryContainer,
                },
            }),
        [colors.onSecondaryContainer, gridSize, roundness],
    );
};

export default OvertimePaidCreateModal;
